import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { App } from './App';
import { AuthProvider } from '@/lib/auth-context';
import { LegalToggleProvider } from '@/components/legal/LegalToggleContext';
import { ToastProvider } from '@/components/ui/Toast';
import { ThemeProvider } from '@/lib/theme';
import { DisplayModeProvider } from '@/lib/display-mode';
import { SoundProvider } from '@/lib/sound';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { isApiError } from '@/lib/api';
import { bootstrapSkills } from '@/skills/_core/bootstrap';
// Direktimport statt Barrel: der Help-Center-Barrel zieht Suchindex und
// Kapitel-Markdown mit, die beim Start niemand braucht.
import { VideoHintProvider } from '@/skills/help-center/VideoHintContext';

// Skills (Plan §18): einmalig vor dem ersten Render registrieren.
bootstrapSkills();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min
      retry: (failureCount, err) => {
        // 4xx never retry
        if (isApiError(err) && err.status >= 400 && err.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root not found');

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <DisplayModeProvider>
              <SoundProvider>
                <AuthProvider>
                  <LegalToggleProvider>
                    <VideoHintProvider>
                      <ToastProvider>
                        <App />
                      </ToastProvider>
                    </VideoHintProvider>
                  </LegalToggleProvider>
                </AuthProvider>
              </SoundProvider>
            </DisplayModeProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
